import { uuidv7 } from 'uuidv7';
import { computed } from 'mobx';
import $auth from 'app/stores/$auth';
import Model from 'core/model/Model';
import api from 'core/util/api';
import { Socket } from 'core/util/index';
import { KAIZEN_EVENT_TYPES, TOOL_CALL_STATUS } from '@kentik/ui-shared/kaizen/constants';

class ChatSessionModel extends Model {
  socket;

  get urlRoot() {
    return '/api/ui/kaizen/chatSession';
  }

  get defaults() {
    return {
      session_title: 'AI Advisor',
      user_id: $auth.getActiveUserProperty('id'),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      messages: [],
      isThinking: false,
      enabledToolConfigs: [],
      session_features: []
    };
  }

  @computed
  get isUserChat() {
    return this.get('user_id') === $auth.getActiveUserProperty('id');
  }

  @computed
  get isKProxyUserChat() {
    return (this.get('user.user_email') || '').startsWith('kproxy-agent');
  }

  @computed
  get hasToolAwaitingApproval() {
    const messages = this.get('messages') || [];
    return messages.some((message) => {
      const agentTurns = message.agentTurn || [];
      return agentTurns.some((turn) => {
        const steps = turn.steps || [];
        return steps.some((step) => {
          const toolCalls = step.toolCalls || [];
          return toolCalls.some((tool) => tool.status === TOOL_CALL_STATUS.AWAITING_APPROVAL || tool.awaitingApproval);
        });
      });
    });
  }

  @computed
  get chatSessionTitle() {
    return this.get('user_title') || this.get('session_title') || 'AI Advisor';
  }

  getUuid() {
    return uuidv7();
  }

  trackChatMessage(options) {
    $auth.track('AI Advisor: Chat Message', {
      message_text: options.message_text,
      id: options.id,
      session_id: options.session_id,
      sender_type: options.sender_type,
      created_at: options.created_at
    });
  }

  startChatSessionAndAddPrompt(promptText, additionalContext, agentOptions, sessionFeatures) {
    const { userTimezone } = $auth;
    this.set('isThinking', true);
    const new_message_id = this.getUuid();
    const session_id = this.get('id');
    const new_session_id = session_id || this.getUuid();

    if (!session_id) {
      this.set('id', new_session_id);
    }

    // Create optimistic user message (include context so user immediately sees current context)

    const optimisticMessage = {
      id: new_message_id,
      session_id: this.get('id'),
      sender_type: 'USER',
      message_text: promptText,
      created_at: new Date().toISOString(),
      agentTurn: [],
      user: $auth.activeUser
    };

    this.set('messages', [optimisticMessage]);
    this.trackChatMessage(optimisticMessage);

    this.selectAndSubscribe();

    const body = {
      new_session_id,
      new_message_id,
      promptText,
      additionalContext,
      userTimezone,
      localTime: new Date().toString(),
      agentOptions
    };

    if (sessionFeatures) {
      body.sessionFeatures = sessionFeatures;
      this.set('session_features', sessionFeatures);
    }

    return api.post(`${this.urlRoot}`, { body });
  }

  addPrompt(promptText, additionalContext, agentOptions) {
    const { userTimezone } = $auth;
    this.set('isThinking', true);
    const new_message_id = this.getUuid();

    // Ensure websocket subscription is active for real-time updates
    this.subscribe();

    // Optimistically add user prompt to messages
    const currentMessages = this.get('messages') || [];

    const optimisticMessage = {
      id: new_message_id,
      session_id: this.get('id'),
      sender_type: 'USER',
      message_text: promptText,
      created_at: new Date().toISOString(),
      user: $auth.activeUser
    };
    this.set('messages', [...currentMessages, optimisticMessage]);

    this.trackChatMessage(optimisticMessage);

    return api
      .put(`${this.urlRoot}`, {
        body: {
          session_id: this.get('id'),
          new_message_id,
          promptText,
          localTime: new Date().toString(),
          userTimezone,
          additionalContext,
          agentOptions
        }
      })
      .then(() => {
        // Optionally update the message with server-confirmed data if necessary,
        // though events should handle most updates.
      })
      .catch((err) => {
        console.error('Error in addPrompt:', err);
        // Revert optimistic update or show error
        this.set('messages', currentMessages);
        // Re-throw the error so upstream handlers can catch it
        throw err;
      });
  }

  selectAndSubscribe(options) {
    this.select();
    this.subscribe();
    return this.select(options).then((data) => {
      // Ensure messages are initialized from fetched data if not already present
      if (data && data.messages && (!this.get('messages') || this.get('messages').length === 0)) {
        this.set('messages', data.messages);
      }
      return data;
    });
  }

  onReceiveEvent = (event) => {
    switch (event.event_type) {
      case KAIZEN_EVENT_TYPES.CHAT_MESSAGE_CREATED:
        this._handleChatMessageCreated(event);
        break;
      case KAIZEN_EVENT_TYPES.CHAT_SESSION_UPDATED:
        this._handleChatSessionUpdated(event);
        break;
      case KAIZEN_EVENT_TYPES.AGENT_TURN_STARTED:
        this._handleAgentTurnStarted(event);
        break;
      case KAIZEN_EVENT_TYPES.AGENT_INITIALIZED:
        // No direct modification to messages structure for this event currently
        break;
      case KAIZEN_EVENT_TYPES.AGENT_STEP_STARTED:
        this._handleAgentStepStarted(event);
        break;
      case KAIZEN_EVENT_TYPES.AGENT_STEP_UPDATED:
        this._handleAgentStepUpdated(event);
        break;
      case KAIZEN_EVENT_TYPES.TOOL_CALL_STARTED:
        this._handleToolCallStarted(event);
        break;
      case KAIZEN_EVENT_TYPES.TOOL_CALL_AWAITING_APPROVAL:
        this._handleToolCallAwaitingApproval(event);
        break;
      case KAIZEN_EVENT_TYPES.TOOL_CALL_APPROVED:
        this._handleToolCallApproved(event);
        break;
      case KAIZEN_EVENT_TYPES.TOOL_CALL_REJECTED:
        this._handleToolCallRejected(event);
        break;
      case KAIZEN_EVENT_TYPES.TOOL_CALL_SUCCEEDED:
        this._handleToolCallSucceeded(event);
        break;
      case KAIZEN_EVENT_TYPES.TOOL_CALL_FAILED:
        this._handleToolCallFailed(event);
        break;
      case KAIZEN_EVENT_TYPES.AGENT_STEP_COMPLETED:
        this._handleAgentStepCompleted(event);
        break;
      case KAIZEN_EVENT_TYPES.AGENT_TURN_COMPLETED:
        this._handleAgentTurnCompleted(event);
        break;
      case KAIZEN_EVENT_TYPES.AGENT_TURN_FAILED:
        this._handleAgentTurnFailed(event);
        break;
      default:
        console.warn('Unknown event_type:', event.event_type);
    }
  };

  _handleChatMessageCreated(event) {
    const messages = this.get('messages') || [];
    // Avoid duplicating the message if it was optimistically added
    if (!messages.find((msg) => msg.id === event.data.id)) {
      this.set('messages', [...messages, { ...event.data, agentTurn: event.data.agentTurn || [] }]);
    } else {
      // If message exists (e.g. optimistic update), update it with server data
      this.set(
        'messages',
        messages.map((msg) =>
          msg.id === event.data.id
            ? { ...msg, ...event.data, agentTurn: msg.agentTurn || event.data.agentTurn || [] }
            : msg
        )
      );
    }
  }

  _handleChatSessionUpdated(event) {
    const title = event?.data?.session_title;
    if (title) {
      this.set('session_title', title);
    }
  }

  _handleOnAgentStep(event) {
    // Handles event listener from Model consumer, i.e. a React component.
    if (typeof this.onAgentStep === 'function') {
      this.onAgentStep(event);
    }
  }

  /**
   * Helper method to update a specific toolCall within the nested message structure.
   * This reduces code duplication across multiple tool call event handlers.
   *
   * @param {Object} event - The event containing user_message_id, agent_turn_id, agent_step_id
   * @param {Function} toolCallMatcher - Function to identify the target toolCall (tc) => boolean
   * @param {Function} toolCallUpdater - Function to update the toolCall (tc) => updatedToolCall
   * @returns {Array} Updated messages array
   */
  _updateToolCall(event, toolCallMatcher, toolCallUpdater) {
    const messages = this.get('messages') || [];
    return messages.map((msg) => {
      if (msg.id === event.user_message_id) {
        const agentTurns = (msg.agentTurn || []).map((turn) => {
          if (turn.id === event.agent_turn_id) {
            const steps = (turn.steps || []).map((step) => {
              if (step.id === event.agent_step_id) {
                const toolCalls = (step.toolCalls || []).map((tc) => {
                  if (toolCallMatcher(tc)) {
                    return toolCallUpdater(tc);
                  }
                  return tc;
                });
                return { ...step, toolCalls };
              }
              return step;
            });
            return { ...turn, steps };
          }
          return turn;
        });
        return { ...msg, agentTurn: agentTurns };
      }
      return msg;
    });
  }

  _handleAgentTurnStarted(event) {
    const messages = this.get('messages') || [];
    const updatedMessages = messages.map((msg) => {
      if (msg.id === event.user_message_id) {
        const agentTurns = msg.agentTurn || [];
        // Avoid duplicating the agent turn
        if (!agentTurns.find((turn) => turn.id === event.data.id)) {
          return { ...msg, agentTurn: [...agentTurns, { ...event.data, steps: event.data.steps || [] }] };
        }
      }
      return msg;
    });
    this.set('messages', updatedMessages);
    this._handleOnAgentStep(event);
  }

  _handleAgentStepStarted(event) {
    const messages = this.get('messages') || [];
    const updatedMessages = messages.map((msg) => {
      if (msg.id === event.user_message_id) {
        const agentTurns = (msg.agentTurn || []).map((turn) => {
          if (turn.id === event.agent_turn_id) {
            const steps = turn.steps || [];
            // Avoid duplicating the step
            if (!steps.find((step) => step.id === event.data.id)) {
              return { ...turn, steps: [...steps, { ...event.data, toolCalls: event.data.toolCalls || [] }] };
            }
          }
          return turn;
        });
        return { ...msg, agentTurn: agentTurns };
      }
      return msg;
    });
    this.set('messages', updatedMessages);
    this._handleOnAgentStep(event);
  }

  _updateStepState(event, isCompletion = false) {
    const messages = this.get('messages') || [];
    const updatedMessages = messages.map((msg) => {
      if (msg.id === event.user_message_id) {
        const agentTurns = (msg.agentTurn || []).map((turn) => {
          if (turn.id === event.agent_turn_id) {
            const steps = (turn.steps || []).map((step) => {
              if (step.id === (isCompletion ? event.data.id : event.agent_step_id) || step.id === event.data.id) {
                return { ...step, ...event.data };
              }
              return step;
            });
            // If step not found and it's a new step completion, add it (edge case)
            if (isCompletion && !steps.find((s) => s.id === event.data.id)) {
              steps.push({ ...event.data, toolCalls: event.data.toolCalls || [] });
            }
            return { ...turn, steps };
          }
          return turn;
        });
        return { ...msg, agentTurn: agentTurns };
      }
      return msg;
    });
    this.set('messages', updatedMessages);
    this._handleOnAgentStep(event);
  }

  _handleAgentStepUpdated(event) {
    this._updateStepState(event);
  }

  _handleAgentStepCompleted(event) {
    this._updateStepState(event, true);
  }

  _handleToolCallStarted(event) {
    // Note: This handler adds a new tool call rather than updating an existing one,
    // so it uses different logic than the _updateToolCall helper
    const messages = this.get('messages') || [];
    const updatedMessages = messages.map((msg) => {
      if (msg.id === event.user_message_id) {
        const agentTurns = (msg.agentTurn || []).map((turn) => {
          if (turn.id === event.agent_turn_id) {
            const steps = (turn.steps || []).map((step) => {
              if (step.id === event.agent_step_id) {
                const toolCalls = step.toolCalls || [];
                // Add new tool call, ensuring not to duplicate
                if (
                  !toolCalls.find(
                    (tc) => tc.tool_tracking_id === event.data.tool_tracking_id && tc.tool_name === event.data.tool_name
                  )
                ) {
                  return { ...step, toolCalls: [...toolCalls, event.data] };
                }
              }
              return step;
            });
            return { ...turn, steps };
          }
          return turn;
        });
        return { ...msg, agentTurn: agentTurns };
      }
      return msg;
    });
    this.set('messages', updatedMessages);
  }

  _handleToolCallSucceeded(event) {
    // Note: Matches on both tool_tracking_id and tool_name for additional safety.
    // Completion events include the full tool data, so we can match on both fields.
    // Use status from event data if provided, otherwise default to SUCCESS
    const updatedMessages = this._updateToolCall(
      event,
      (tc) => tc.tool_tracking_id === event.data.tool_tracking_id && tc.tool_name === event.data.tool_name,
      (tc) => ({ ...tc, ...event.data, status: event.data.status || TOOL_CALL_STATUS.SUCCESS })
    );
    this.set('messages', updatedMessages);
    this._handleOnToolResult(event);
  }

  _handleOnToolResult(event) {
    if (typeof this.onToolResult === 'function') {
      this.onToolResult(event);
    }
  }

  _handleToolCallFailed(event) {
    // Note: Matches on both tool_tracking_id and tool_name for additional safety.
    // Completion events include the full tool data, so we can match on both fields.
    // Use status from event data if provided, otherwise default to FAILED
    const updatedMessages = this._updateToolCall(
      event,
      (tc) => tc.tool_tracking_id === event.data.tool_tracking_id && tc.tool_name === event.data.tool_name,
      (tc) => ({ ...tc, ...event.data, status: event.data.status || TOOL_CALL_STATUS.FAILED })
    );
    this.set('messages', updatedMessages);
  }

  _handleToolCallAwaitingApproval(event) {
    // Note: Only matches on tool_tracking_id as approval events may not include tool_name
    const updatedMessages = this._updateToolCall(
      event,
      (tc) => tc.tool_tracking_id === event.data.tool_tracking_id,
      (tc) => ({ ...tc, ...event.data, status: TOOL_CALL_STATUS.AWAITING_APPROVAL, awaitingApproval: true })
    );
    this.set('messages', updatedMessages);
  }

  _handleToolCallApproved(event) {
    // Note: Only matches on tool_tracking_id as approval events may not include tool_name
    // Use status from event data if provided, otherwise default to USER_APPROVED
    const updatedMessages = this._updateToolCall(
      event,
      (tc) => tc.tool_tracking_id === event.data.tool_tracking_id,
      (tc) => ({ ...tc, status: event.data.status || TOOL_CALL_STATUS.USER_APPROVED, awaitingApproval: false })
    );
    this.set('messages', updatedMessages);
  }

  _handleToolCallRejected(event) {
    // Note: Only matches on tool_tracking_id as approval events may not include tool_name
    // Use status from event data if provided, otherwise default to USER_REJECTED
    const updatedMessages = this._updateToolCall(
      event,
      (tc) => tc.tool_tracking_id === event.data.tool_tracking_id,
      (tc) => ({
        ...tc,
        status: event.data.status || TOOL_CALL_STATUS.USER_REJECTED,
        awaitingApproval: false,
        error_message: event.data.error_message
      })
    );
    this.set('messages', updatedMessages);
  }

  _handleAgentTurnCompleted(event) {
    const messages = this.get('messages') || [];
    const updatedMessages = messages.map((msg) => {
      if (msg.id === event.user_message_id) {
        const agentTurns = (msg.agentTurn || []).map((turn) => {
          if (turn.id === event.data.id) {
            // event.data.id is the agent_turn_id for this event
            return { ...turn, ...event.data };
          }
          return turn;
        });
        // If the agent turn wasn't found (e.g. if agent_turn_started was missed), add it.
        if (!agentTurns.find((t) => t.id === event.data.id) && msg.agentTurn) {
          agentTurns.push({ ...event.data, steps: event.data.steps || [] });
        } else if (!msg.agentTurn) {
          // if agentTurn array itself doesn't exist
          msg.agentTurn = [{ ...event.data, steps: event.data.steps || [] }];
        }
        return { ...msg, agentTurn: agentTurns };
      }
      return msg;
    });
    this.set('messages', updatedMessages);
    this.set('isThinking', false);
  }

  _handleAgentTurnFailed(event) {
    const messages = this.get('messages') || [];
    const updatedMessages = messages.map((msg) => {
      if (msg.id === event.user_message_id) {
        const agentTurns = (msg.agentTurn || []).map((turn) => {
          if (turn.id === event.data.id) {
            // event.data.id is the agent_turn_id for this event
            return { ...turn, ...event.data, status: 'FAILED' };
          }
          return turn;
        });
        // If the agent turn wasn't found (e.g. if agent_turn_started was missed), add it as failed.
        if (!agentTurns.find((t) => t.id === event.data.id) && msg.agentTurn) {
          agentTurns.push({ ...event.data, steps: event.data.steps || [], status: 'FAILED' });
        } else if (!msg.agentTurn) {
          // if agentTurn array itself doesn't exist
          msg.agentTurn = [{ ...event.data, steps: event.data.steps || [], status: 'FAILED' }];
        }
        return { ...msg, agentTurn: agentTurns };
      }
      return msg;
    });
    this.set('messages', updatedMessages);
    this.set('isThinking', false);
  }

  subscribe() {
    if (!this.id) {
      return;
    }

    // If there's an existing socket that is no longer listening (e.g. after unsubscribe),
    // clean it up so we can create a fresh one
    if (this.socket && !this.socket.listening) {
      this.socket = null;
    }

    if (this.socket) {
      return;
    }

    const socket = new Socket({
      outType: 'kaizenJoinSession',
      inType: `chatSession:${this.id}`,
      frequency: 60,
      delaySend: true,
      listenToBroadcast: true,
      onSuccess: this.onReceiveEvent,
      onError(err) {
        console.warn('Received Kaizen Subscription Socket Error', err);
      },
      onReconnect: () => {
        socket.setOutType('kaizenJoinSession');
        socket.send({ session_id: this.id });
        this.fetch();
      }
    });

    this.socket = socket;
    this.socket.send({ session_id: this.id });
  }

  unsubscribe() {
    try {
      if (this.socket) {
        this.socket.cancelAll();
        // Note: Do NOT call socket.destroy() here — it disconnects the shared
        // singleton socket.io connection used by the entire application.
        // cancelAll() is sufficient to remove listeners for this channel.
        this.socket = null;
      }
    } catch (e) {
      // We can ignore this, as error usually means it wasn't open yet
    }
  }

  approveToolCall(toolTrackingId, approved) {
    const session_id = this.get('id');
    // Send approval message via socket instead of HTTP API
    if (this.socket) {
      // Temporarily change the outType to send approval message
      this.set('isThinking', true);
      const originalOutType = this.socket.outType;
      this.socket.setOutType('kaizenApproveToolCall');
      this.socket.send({
        session_id,
        tool_tracking_id: toolTrackingId,
        approved,
        approval_sent_at: Date.now()
      });
      // Restore the original outType
      this.socket.setOutType(originalOutType);
      return Promise.resolve();
    }
    return Promise.reject(new Error('Socket not connected'));
  }

  // Custom method to update session title using the new API endpoint
  async updateSessionTitle(newTitle) {
    return api
      .put(`${this.urlRoot}/${this.id}`, {
        body: { user_title: newTitle }
      })
      .then((response) => {
        this.set('user_title', newTitle);
        this.set('updated_at', new Date().toISOString());
        return response;
      });
  }

  // Override save method to use custom endpoint for updates
  async save(attributes = {}, options = {}) {
    if (this.id && options.patch && (attributes.session_title || attributes.user_title)) {
      // Use the custom update endpoint for title updates
      // Support both session_title and user_title for backwards compatibility
      const newTitle = attributes.user_title || attributes.session_title;
      return this.updateSessionTitle(newTitle);
    }
    // Fall back to default save behavior for new sessions
    return super.save(attributes, options);
  }
}

export default ChatSessionModel;
